import { useCallback, useEffect, useRef, useState } from 'react';
import Cropper from 'react-easy-crop';
// The cropper normally injects this stylesheet at runtime, which our Content-Security-Policy (style-src 'self') blocks.
import 'react-easy-crop/react-easy-crop.css';
import { Modal } from '../../components/Modal';
import { pickNativePhoto, photosViaCameraPlugin } from '../../lib/native';
import { defaultCropPercent, normalize, encodePhoto, type CropArea, type EncodedPhoto } from '../../lib/photo';
import { Portrait } from '../canvas/PersonCard';
import type { Person } from '@lineage/core';
import { useT } from '../../i18n';

interface Props {
  person: Person;
  value: EncodedPhoto | null;
  onChange: (p: EncodedPhoto | null) => void;
  /** Removing the current photo (edit mode). */
  onRemoveExisting?: () => void;
}

export function PhotoPicker({ person, value, onChange, onRemoveExisting }: Props) {
  const t = useT();
  const input = useRef<HTMLInputElement>(null);
  const [src, setSrc] = useState<{ url: string; w: number; h: number; file: Blob } | null>(null);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [area, setArea] = useState<CropArea | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => () => { if (src) URL.revokeObjectURL(src.url); }, [src]);

  const choose = async (file: File | undefined) => {
    if (!file) return;
    setError('');
    try {
      // Work from an upright, downsized copy so what's shown and what's cropped are exactly the same picture.
      const n = await normalize(file);
      setSrc({ url: URL.createObjectURL(n.blob), w: n.w, h: n.h, file: n.blob });
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setArea(null);
    } catch {
      setError(t.photo.couldntOpen);
    }
  };

  const onComplete = useCallback((_: unknown, px: CropArea) => setArea(px), []);

  const confirm = async () => {
    if (!src || !area) return;
    setBusy(true);
    try {
      onChange(await encodePhoto(src.file, area));
      setSrc(null);
    } catch {
      setError(t.photo.couldntProcess);
    } finally {
      setBusy(false);
    }
  };

  const initial = src ? defaultCropPercent(src.w, src.h) : undefined;
  const hasPhoto = Boolean(value) || Boolean(person.photoId);
  return (
    <div className="row">
      <Portrait person={person} override={value?.previewUrl} />
      <div className="stack" style={{ gap: 8 }}>
        <button type="button" className="btn btn-sm" onClick={() => (photosViaCameraPlugin ? void pickNativePhoto().then((f) => choose(f ?? undefined)) : input.current?.click())}>
          {hasPhoto ? t.photo.replace : t.photo.add}
        </button>
        {hasPhoto && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              onChange(null);
              onRemoveExisting?.();
            }}
          >
            {t.photo.remove}
          </button>
        )}
        {error && <span className="error">{error}</span>}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        hidden
        onChange={(e) => {
          void choose(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
      {src && (
        <Modal title={t.photo.crop} onClose={() => setSrc(null)} full>
          <div style={{ position: 'relative', height: 'min(60dvh, 420px)', background: '#000', borderRadius: 16, overflow: 'hidden' }}>
            <Cropper
              image={src.url}
              crop={crop}
              zoom={zoom}
              disableAutomaticStylesInjection
              minZoom={1}
              maxZoom={4}
              aspect={1}
              cropShape="round"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={onComplete}
              initialCroppedAreaPercentages={initial}
            />
          </div>
          <label className="field" style={{ marginTop: 16 }}>
            <span>{t.photo.zoom}</span>
            <input type="range" min={1} max={4} step={0.05} value={zoom} onChange={(e) => setZoom(Number(e.target.value))} />
          </label>
          <div className="row" style={{ marginTop: 16, justifyContent: 'flex-end' }}>
            <button className="btn" type="button" onClick={() => setSrc(null)}>{t.common.cancel}</button>
            <button className="btn btn-primary" type="button" disabled={busy || !area} onClick={confirm}>{t.photo.use}</button>
          </div>
        </Modal>
      )}
    </div>
  );
}
